<!--

	Page that creates the frequently asked question and the answer to this question

-->
<template>
	<div ref="question" class="mia-AnsweredQuestion">
		<div :class="['mia-question', {'expanded': expanded }]">
			<expandable v-model="expanded">
				<div class="mia-questionWrapper">
					<p> 
						{{ item.Question }}
					</p>
				</div>
			</expandable>
			<p v-if="expanded" class="mia-answer"> 
				{{ item.Answer }}
			</p>
		</div>
	</div>
</template>
<script lang="ts">
import { Component, Prop, toNative } from "vue-facing-decorator";
import { Item } from "@/Control/Vue/Lists/VueList/Items/Item";
import Expandable from "@/curriculum/common/Expandable.vue";
import { DefaultAnsweredQuestion } from "@/Control/Vue/Defaults";
import { MiaPlaza } from "@/Reinforced.Typings";

@Component({
	components: {
		Expandable
	}
})
export class AnsweredQuestion extends Item<MiaPlaza.ConfigModel.FAQ.IAnsweredQuestion> {
	@Prop({ required: true, type: String }) private searchQuery!: string;
	@Prop({ required: false, default: null }) private expandedQuestionWhenSearchQueryIsEmpty!: string | null;
	@Prop({ required: true, type: Function }) private setExpandedQuestionWhenSearchQueryIsEmpty!: (question: string | null) => void;
	@Prop({ required: false, type: Object, default: null }) private targetQuestion!: MiaPlaza.ConfigModel.FAQ.IAnsweredQuestion | null;

	protected defaults = DefaultAnsweredQuestion;

	protected mounted() {
		this.scrollToTarget();
	}

	private get isTargetQuestion() {
		return this.targetQuestion?.Question === this.item.Question
			&& this.targetQuestion?.Answer === this.item.Answer;
	}

	private get navbarOffset() {
		// We need to compensate the height of the navbar with extra margin to make the target
		// question clearly seen. We use main's top margin as it's defined for the same reason
		const main = document.getElementById("mainContent") as HTMLElement;
		const mainMarginTop = window.getComputedStyle(main).getPropertyValue('margin-top');
		return parseInt(mainMarginTop);
	}

	private get scrollOffset() {
		const targetQuestion = this.$refs.question as HTMLElement;
		const targetQuestionPosition = targetQuestion.getBoundingClientRect().top + window.pageYOffset;
		return targetQuestionPosition - this.navbarOffset;
	}

	private scrollToTarget() {
		if (!this.isTargetQuestion) {
			return;
		}

		this.expanded = true;

		window.scrollTo({
			top: this.scrollOffset,
			behavior: 'smooth'
		});
	}

	// isQuestionExpandedWhenSearchQueryIsNotEmpty is needed because we want to receive the questions 
	// with expanded answers after we write something in the search
	protected isQuestionExpandedWhenSearchQueryIsNotEmpty = true;

	get expanded(): boolean {
		if (this.searchQuery.length) {
			return this.isQuestionExpandedWhenSearchQueryIsNotEmpty;
		}
		return this.identifier === this.expandedQuestionWhenSearchQueryIsEmpty;
	}

	protected set expanded(value: boolean) {
		if (this.searchQuery.length) {
			// isQuestionExpandedWhenSearchQueryIsNotEmpty is changed there because 
			// we want the possibility to hide some expanded answers
			this.isQuestionExpandedWhenSearchQueryIsNotEmpty = !this.isQuestionExpandedWhenSearchQueryIsNotEmpty;
		} else if (value) {
			this.setExpandedQuestionWhenSearchQueryIsEmpty((this.from as any).from);
		} else {
			this.setExpandedQuestionWhenSearchQueryIsEmpty(null);
		}
	}
}

export default toNative(AnsweredQuestion);
</script>
<style lang="scss">
.mia-AnsweredQuestion {
	.mia-question {
		padding-left: theme.$mia-padding-panel-large;
		border: theme.$mia-border-width-panel-small solid theme.$mia-color-expandableRow-border;

		&.expanded {
			background-color: theme.$mia-color-contrastPanel;
			color: theme.$mia-color-contrastPanel-font;
			padding-bottom: theme.$mia-padding-panel;
		}

		.mia-Expandable {
			display: flex;

			.mia-questionWrapper {
				flex-grow: 1;
				display: flex;
				align-items: center;
			}
		}

		.mia-answer {
			white-space: pre-line;
			padding-right: theme.$mia-padding-panel-large;
		}
	}

	p {
		margin-top: theme.$mia-margin-size;
		margin-bottom: theme.$mia-margin-size;
	}
}
</style>
